<template>
  <div v-if="internalShow || show" class="modal new-modal show walkthrough-modal" @click.self="skipTour">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <!-- Skip Tour Button -->
        <button class="btn btn-white skip-tour-btn" @click="skipTour">
          Skip Tour
        </button>

      <!-- Slide 1: Welcome -->
      <div v-if="currentStep === 1" class="slide-welcome">
        <div class="slide-icon-wrapper">
          <div class="slide-icon">
            <img src="/img/icons/nav-icons/Playbooks.svg">
          </div>
        </div>
        <h2 class="welcome-title">Welcome to Playbooks!</h2>
        <p class="welcome-subtitle">Your campaigns just got smarter.</p>
        <p class="welcome-description">
          Create high-performing fundraising journeys in seconds, powered by your data and Avid's intelligence.
        </p>
        <button @click="nextSlide" class="btn btn-white btn-next">Next</button>
      </div>

      <!-- Slides 2-8: Dynamic format -->
      <div v-else-if="currentStep >= 2 && currentStep <= 8" class="slide-content">
        <!-- Side by side: Bootstrap 2-column layout -->
        <div v-if="currentSlide.format === 'side by side'" class="row align-items-center">
          <div class="col-12 col-md-6 slide-text-col">
            <div v-html="formatText(currentSlide.text)"></div>
          </div>
          <div class="col-12 col-md-6 slide-image-col">
            <div class="image-placeholder">
              <img :src="currentSlide.image" class="slide-image" alt="Slide image" />
            </div>
          </div>
        </div>

        <!-- Up and down: Vertical stack layout -->
        <div v-else class="format-up-and-down">
          <div class="slide-text" v-html="formatText(currentSlide.text)"></div>
          <div class="slide-image-container">
            <div class="image-placeholder">
              <img :src="currentSlide.image" class="slide-image" alt="Slide image" />
            </div>
          </div>
        </div>
      </div>

      <!-- Slide 9: Final CTA -->
      <div v-else class="slide-final">
        <div class="slide-icon-wrapper">
          <div class="slide-icon">
            <img src="/img/icons/nav-icons/Playbooks.svg">
          </div>
        </div>
        <p class="final-subtitle mb-0">You're ready to build your first Play.</p>
        <h2 class="final-title mb-3">Let's get started.</h2>
      </div>

      <!-- Navigation (bottom) - show for content slides (steps 2-8) and final slide (step 9) -->
      <div v-if="currentStep >= 2 && currentStep <= 9" class="slide-navigation">
        <button 
          @click="prevSlide" 
          class="btn-nav-prev"
          :disabled="currentStep === 2"
          data-bs-toggle="tooltip"
          title="Previous">
          <i class="fa fa-chevron-left"></i>
        </button>
        
        <!-- Pagination dots (7 dots for steps 2-8 only) -->
        <div v-if="currentStep >= 2 && currentStep <= 8" class="pagination-dots">
          <span 
            v-for="n in 7" 
            :key="n" 
            :class="['dot', { active: n === currentStep - 1 }]"
            @click="goToStep(n + 1)">
          </span>
        </div>
        
        <button 
          v-if="currentStep < 9"
          @click="nextSlide" 
          class="btn btn-white btn-next-nav">
          Next
        </button>
        <button 
          v-else
          @click="completeTour" 
          class="btn btn-primary btn-continue">
          Continue
        </button>
      </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PlaybookWalkThroughSlider',
  
  props: {
    show: {
      type: Boolean,
      default: false
    },
    clientUrl: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      internalShow: false,
      currentStep: 1,
      slides: [
        {
          step: 1,
          format: 'welcome',
          text: 'Welcome to Playbooks!',
          image: ''
        },
        {
          step: 2,
          format: 'side by side',
          text: '<h3>Run Plays</h3><p>A Play is where ideas turn into action. Define your goal, and Avid builds the strategy around it. Audience, messaging, and timing included. Every Play is designed to launch fast, learn fast, and make your next campaign even smarter.</p>',
          image: '/img/images/playbook-slider-3.png'
        },
        {
          step: 3,
          format: 'side by side',
          text: '<h3>Dive Deeper</h3><p>Click to open a detailed view with extended insights, explanations, and data. It\'s where you\'ll find the full story behind what you\'re seeing on screen, designed for when you want more than a surface-level view.</p>',
          image: '/img/images/playbook-slider-2.png'
        },
        {
          step: 4,
          format: 'up and down',
          text: '<h3>Playbooks Dashboard</h3><p>Your Playbook dashboard keeps every campaign within reach. Active Plays are running now. Draft Plays are in progress. Previous Plays hold past campaigns and insights. Experiment Plays test new ideas using behavioral psychology to see what drives the strongest response.</p>',
          image: '/img/images/playbook-slider-4.png'
        },      
        {
          step: 5,
          format: 'side by side',
          text: '<h3>Meet Edna</h3><p>Your <u>E</u>nhanced <u>D</u>onor <u>N</u>etwork <u>A</u>ssistant. She learns from your data, builds your campaigns, and refines every detail over time. Faster strategy, smarter insights, stronger results.</p>',
          image: '/img/images/playbook-slider-5.png'
        },
        {
          step: 6,
          format: 'side by side',
          text: '<h3>Strategy</h3><p>Your Strategy sets the direction. Edna builds it for you, using your data to craft goals, audiences, and messaging that align with your campaign’s purpose.</p>',
          image: '/img/images/playbook-slider-6.png'
        },
        {
          step: 7,
          format: 'side by side',
          text: '<h3>Audience</h3><p>Your Audience defines who this Play is segmented for. Edna segments your data to target the right people with the right message at the right time. You can adjust the default segments to better fit your audience.</p>',
          image: '/img/images/playbook-slider-7.png'
        },
        {
          step: 8,
          format: 'side by side',
          text: '<h3>Touchpoints</h3><p>Touchpoints are the moments your audience experiences your message. Edna creates and connects them through emails, ads, social posts, and more into one cohesive campaign.</p>',
          image: '/img/images/playbook-slider-8.png'
        },
        {
          step: 9,
          format: 'final',
          text: 'You\'re ready to build your first Play.',
          image: ''
        }
      ]
    };
  },

  computed: {
    currentSlide() {
      return this.slides[this.currentStep - 1];
    }
  },

  methods: {
    nextSlide() {
      if (this.currentStep < 9) {
        this.currentStep++;
      } else {
        this.completeTour();
      }
    },

    prevSlide() {
      // Don't allow going back to step 1 (welcome screen)
      if (this.currentStep > 2) {
        this.currentStep--;
      }
    },

    goToStep(step) {
      this.currentStep = step;
    },

    skipTour() {
      this.submitOptIn();
    },

    completeTour() {
      this.submitOptIn();
    },

    submitOptIn() {
      // Submit opt-in via API and redirect on success
      window.axios.post(`/api/playbooks/${this.clientUrl}/opt-in`)
        .then(response => {
          // Redirect to playbooks list on success
          window.location.href = response.data.redirect_url;
        })
        .catch(error => {
          console.error('Opt-in failed:', error);
        });
    },

    formatText(text) {
      return text;
    }
  },

  mounted() {
    // Listen for global event to show walkthrough
    this.showWalkthroughHandler = () => {
      this.internalShow = true;
      this.currentStep = 1;
      document.body.style.overflow = 'hidden';
    };
    window.addEventListener('show-walkthrough', this.showWalkthroughHandler);
  },

  watch: {
    show(newVal) {
      if (newVal) {
        // Reset to first slide when modal opens
        this.currentStep = 1;
        // Prevent body scroll when modal is open
        document.body.style.overflow = 'hidden';
      } else {
        // Restore body scroll
        document.body.style.overflow = '';
      }
    },
    
    internalShow(newVal) {
      if (!newVal) {
        // Restore body scroll when internally closed
        document.body.style.overflow = '';
      }
    }
  },

  beforeUnmount() {
    // Cleanup: restore body scroll and remove event listener
    document.body.style.overflow = '';
    if (this.showWalkthroughHandler) {
      window.removeEventListener('show-walkthrough', this.showWalkthroughHandler);
    }
  }
};
</script>

